'use client';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ArrowRight, Eye, EyeOff, Lock, Mail } from 'lucide-react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useState, useTransition } from 'react';

import { GoogleSignInButton } from '@/components/google-sign-in-button';

export function LoginForm() {
  const router = useRouter();
  const params = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, rememberMe }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      if (body.error === 'account_uses_google') {
        setError('account_uses_google');
      } else {
        setError(body.error ?? 'Login gagal');
      }
      return;
    }
    const next = params.get('next') ?? '/dashboard';
    startTransition(() => {
      router.push(next as any);
      router.refresh();
    });
  }

  const resetOk = params.get('reset') === 'ok';
  const googleFailed = params.get('google') === 'failed';
  const deleted = params.get('deleted') === '1';

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {resetOk && (
        <div
          role="status"
          aria-live="polite"
          className="rounded-xl bg-sage-lt p-3 text-sm text-sage-dk"
        >
          Password berhasil diubah. Silakan masuk dengan password baru.
        </div>
      )}
      {googleFailed && (
        <div
          role="status"
          aria-live="polite"
          className="rounded-xl bg-rose/10 p-3 text-sm text-rose"
        >
          Login dengan Google gagal. Silakan coba lagi atau gunakan email + password.
        </div>
      )}
      {deleted && (
        <div
          role="alert"
          aria-live="polite"
          className="rounded-xl bg-green-ok/10 p-3 text-sm text-green-ok"
        >
          Akun berhasil dihapus.
        </div>
      )}

      {/* Google sign-in — at the top */}
      <GoogleSignInButton />

      {/* Divider */}
      <div className="flex items-center gap-3 my-4">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs lg:text-sm font-body text-brown-lt">atau masuk dengan email</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      {/* Email input with icon prefix */}
      <div>
        <label
          htmlFor="email"
          className="block text-sm lg:text-base font-semibold mb-1.5 font-body text-brown"
        >
          Email
        </label>
        <div className="relative">
          <Mail className="w-[18px] h-[18px] absolute left-3.5 top-1/2 -translate-y-1/2 text-brown-lt pointer-events-none" />
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="contoh@email.com"
            className="w-full py-3 pl-11 pr-4 rounded-xl border-[1.5px] border-border bg-white text-brown font-body text-sm focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/20 transition-colors"
          />
        </div>
      </div>

      {/* Password input with icon prefix + show/hide toggle */}
      <div>
        <label
          htmlFor="password"
          className="block text-sm lg:text-base font-semibold mb-1.5 font-body text-brown"
        >
          Password
        </label>
        <div className="relative">
          <Lock className="w-[18px] h-[18px] absolute left-3.5 top-1/2 -translate-y-1/2 text-brown-lt pointer-events-none" />
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Masukkan password"
            className="w-full py-3 pl-11 pr-11 rounded-xl border-[1.5px] border-border bg-white text-brown font-body text-sm focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/20 transition-colors"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-brown-lt p-1 hover:text-brown-md transition-colors"
          >
            {showPassword ? (
              <EyeOff className="w-[18px] h-[18px]" />
            ) : (
              <Eye className="w-[18px] h-[18px]" />
            )}
          </button>
        </div>
      </div>

      {/* Remember me + forgot password */}
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="w-4 h-4 rounded border-border text-sage accent-sage"
          />
          <span className="text-sm lg:text-base font-body text-brown-md">Ingat saya</span>
        </label>
        <Link
          href={'/forgot-password' as any}
          className="text-sm lg:text-base font-body font-semibold text-sage hover:text-sage-dk transition-colors"
        >
          Lupa Password?
        </Link>
      </div>

      {/* Error display */}
      {error === 'account_uses_google' ? (
        <div role="status" aria-live="polite" className="rounded-xl bg-sand p-3 text-sm text-brown">
          Akun ini terdaftar dengan Google. Silakan masuk dengan tombol{' '}
          <span className="font-semibold">Lanjutkan dengan Google</span> di atas.
        </div>
      ) : error ? (
        <p className="text-sm text-rose">{error}</p>
      ) : null}

      {/* Submit button — gradient coral→rose with arrow */}
      <button
        type="submit"
        disabled={pending}
        className="w-full py-3.5 rounded-xl font-body font-bold text-white bg-gradient-to-br from-coral to-rose hover:from-rose hover:to-coral transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {pending ? (
          'Memproses…'
        ) : (
          <>
            Masuk
            <ArrowRight className="w-[18px] h-[18px]" />
          </>
        )}
      </button>
    </form>
  );
}
